import { cn } from '@/lib/utils'
import type { HomeItemIcon, SectionTheme } from '@/models/homepage-config/homepage-config'
import {
  Bot,
  Image as ImageIcon,
  Layers,
  type LucideIcon,
  Music,
  Rocket,
  Sparkles,
  Star,
  Video,
  Wand2,
  Zap,
} from 'lucide-react'
import Link from 'next/link'
import type { MouseEventHandler, ReactNode } from 'react'
import { AutoplayVideo } from './AutoplayVideo'

const VIDEO_URL = /\.(mp4|webm|mov|m4v)(\?|#|$)/i

export const HOME_ICONS: Record<HomeItemIcon, LucideIcon> = {
  sparkles: Sparkles,
  zap: Zap,
  wand: Wand2,
  layers: Layers,
  bot: Bot,
  rocket: Rocket,
  image: ImageIcon,
  video: Video,
  music: Music,
  star: Star,
}

/** Full-surface treatments a CMS editor can pick per section (`section.theme`). */
export const THEME_SURFACE: Record<SectionTheme, string> = {
  dark: 'bg-card text-foreground dark:bg-ink-raised dark:text-foreground',
  purple: 'bg-brand-deep text-white',
  pink: 'bg-brand-accent text-ink',
  gradient: 'bg-gradient-to-br from-brand-deep via-brand-accent to-brand-glow text-white',
  ocean: 'bg-[linear-gradient(105deg,#14b8a6_0%,#0e7490_32%,#123a7a_68%,#0b1f55_100%)] text-white',
}

/** Image or muted looping video, decided by file extension — CMS editors just paste a URL. */
export function HomeMedia({ src, alt = '', className }: { src?: string | null; alt?: string; className?: string }) {
  if (!src) return <div aria-hidden className={cn('bg-muted dark:bg-ink-raised', className)} />
  if (VIDEO_URL.test(src)) {
    return <AutoplayVideo src={src} className={className} label={alt || undefined} />
  }
  return <img src={src} alt={alt} className={className} loading="lazy" decoding="async" />
}

/** Internal paths use client navigation; anything else opens in a new tab. */
export function SmartLink({ href, className, children, ...rest }: { href?: string | null; className?: string; children: ReactNode; 'aria-label'?: string; onClick?: MouseEventHandler<HTMLAnchorElement> }) {
  if (!href) return <div className={className}>{children}</div>
  if (href.startsWith('/')) {
    return <Link href={href} className={className} {...rest}>{children}</Link>
  }
  return <a href={href} className={className} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>
}

export function SectionHeading({ title, subtitle, className }: { title?: string | null; subtitle?: string | null; className?: string }) {
  if (!title && !subtitle) return null
  return (
    <header
      className={cn('mb-6 flex flex-col gap-1', title && 'pt-4 md:pt-6', className)}
    >
      {title && (
        <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-tight text-brand-accent md:text-4xl">{title}</h2>
      )}
      {subtitle && <p className="text-sm text-muted-foreground md:text-base">{subtitle}</p>}
    </header>
  )
}

/**
 * Chunky CTA with a hard offset shadow — the "slam" button. `tone="ink"` is for use on
 * pink surfaces where a pink button would vanish.
 */
export function PunchButton({ href, children, tone = 'pink', className, onClick }: { href?: string | null; children: ReactNode; tone?: 'pink' | 'ink' | 'white'; className?: string; onClick?: MouseEventHandler<HTMLAnchorElement> }) {
  const tones = {
    pink: 'bg-brand-accent text-ink shadow-[0_5px_0_0_var(--color-brand-deep)] hover:bg-brand-glow',
    ink: 'bg-ink text-brand-accent shadow-[0_5px_0_0_var(--color-brand-deep)] hover:bg-ink-raised',
    white: 'bg-white text-ink shadow-[0_5px_0_0_var(--color-brand-accent)] hover:bg-brand-soft',
  }
  return (
    <SmartLink
      href={href || '/'}
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-base font-semibold transition-[transform,background-color,box-shadow] duration-150',
        'hover:-translate-y-0.5 active:translate-y-[3px] active:shadow-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-soft',
        tones[tone],
        className,
      )}
    >
      {children}
    </SmartLink>
  )
}
